import React from "react";

import { zeosDialog } from "../Shared/confirm-dialog/ConfirmDialog";
import { ZeosCheckboxGroup } from "../Shared/ZeosCheckboxGroup";
import { ZeosRadioGroup } from "../Shared/ZeosRadioGroup";
import { ISurveyQuestion } from "../../services/zeos-api/contracts/Survey";
import { QuestionTypeEnum } from "../../services/zeos-api/enums/Survey";
import { isQuestionType } from "../../services/survey/SurveyVisibilityHelpers";

interface IProps {
  question: ISurveyQuestion;
  isGate: boolean;
  answers: Record<number, any>;
  setAnswers: React.Dispatch<React.SetStateAction<Record<number, any>>>;
}

const RANK_LABELS = ["Primary", "Secondary", "Tertiary"];

export const SurveyQuestion = (props: IProps) => {
  const { question, isGate, answers, setAnswers } = props;

  function handleSingleSelect(questionId: number, value: number): void {
    setAnswers({
      ...answers,
      [questionId]: Number(value),
    });
  }

  function handleMultiSelect(
    questionId: number,
    selectedOptions: number[]
  ): void {
    const maxSelect = question.maxSelect;
    if (maxSelect != null && selectedOptions.length > maxSelect) {
      zeosDialog(`Please select up to ${maxSelect}.`);
      return;
    }
    setAnswers({
      ...answers,
      [questionId]: selectedOptions.map((id) => Number(id)),
    });
  }

  function handleRankedSelect(
    questionId: number,
    selectedLabels: string[]
  ): void {
    const maxSelect = question.maxSelect ?? question.options.length;
    const previous: number[] = Array.isArray(answers[questionId])
      ? answers[questionId].map((id: number) => Number(id))
      : [];

    const selectedIds = question.options
      .filter((opt) => selectedLabels.includes(opt.displayName))
      .map((opt) => Number(opt.responseOptionId));

    // Preserve prior rank order for still-selected options; append newly selected at end.
    const ordered = previous.filter((id) => selectedIds.includes(id));
    selectedIds.forEach((id) => {
      if (!ordered.includes(id)) {
        ordered.push(id);
      }
    });

    if (ordered.length > maxSelect) {
      zeosDialog(`Please select and rank up to ${maxSelect}.`);
      return;
    }

    setAnswers({
      ...answers,
      [questionId]: ordered,
    });
  }

  function handleFreeTextChange(questionId: number, value: string): void {
    const maxLength = question.maxLength;
    const nextValue =
      maxLength != null && value.length > maxLength
        ? value.slice(0, maxLength)
        : value;
    setAnswers({ ...answers, [questionId]: nextValue });
  }

  const selectedOptionId = answers[question.questionId];
  const selectedLabel =
    question.options.find(
      (opt) => Number(opt.responseOptionId) === Number(selectedOptionId)
    )?.displayName || "";

  const rankedIds: number[] = Array.isArray(answers[question.questionId])
    ? answers[question.questionId].map((id: number) => Number(id))
    : [];

  return (
    <div>
      {isQuestionType(question.questionTypeId, QuestionTypeEnum.SingleChoice) && (
        <ZeosRadioGroup
          name={`q-${question.questionId}`}
          options={question.options.map((opt) => opt.displayName)}
          value={selectedLabel}
          onChange={(selectedLabelValue) => {
            const option = question.options.find(
              (opt) => opt.displayName === selectedLabelValue
            );
            if (option) {
              handleSingleSelect(question.questionId, option.responseOptionId);
            }
          }}
          layout={isGate ? "horizontal" : "vertical"}
        />
      )}

      {isQuestionType(question.questionTypeId, QuestionTypeEnum.MultiChoice) && (
        <ZeosCheckboxGroup
          options={question.options.map((opt) => opt.displayName)}
          selected={
            question.options
              .filter((opt) =>
                (answers[question.questionId] || [])
                  .map((id: number) => Number(id))
                  .includes(Number(opt.responseOptionId))
              )
              .map((opt) => opt.displayName) || []
          }
          onChange={(selectedLabels) => {
            const selectedIds = question.options
              .filter((opt) => selectedLabels.includes(opt.displayName))
              .map((opt) => Number(opt.responseOptionId));
            handleMultiSelect(question.questionId, selectedIds);
          }}
          layout={isGate ? "horizontal" : "vertical"}
        />
      )}

      {isQuestionType(
        question.questionTypeId,
        QuestionTypeEnum.RankedChoice
      ) && (
        <div className="space-y-3">
          <ZeosCheckboxGroup
            options={question.options.map((opt) => opt.displayName)}
            selected={question.options
              .filter((opt) =>
                rankedIds.includes(Number(opt.responseOptionId))
              )
              .map((opt) => opt.displayName)}
            onChange={(selectedLabels) =>
              handleRankedSelect(question.questionId, selectedLabels)
            }
            layout="vertical"
          />
          {rankedIds.length > 0 && (
            <ol className="space-y-1 text-sm text-zeos-black list-none pl-0">
              {rankedIds.map((optionId, index) => {
                const label =
                  question.options.find(
                    (opt) => Number(opt.responseOptionId) === optionId
                  )?.displayName || "";
                const rankLabel =
                  RANK_LABELS[index] || `Rank ${index + 1}`;
                return (
                  <li
                    key={optionId}
                    className="flex items-start gap-2 border border-zeos-grey-lt rounded-md px-3 py-2"
                  >
                    <span className="font-semibold text-zeos-orange-dk min-w-[5.5rem]">
                      {rankLabel}
                    </span>
                    <span>{label}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}

      {isQuestionType(question.questionTypeId, QuestionTypeEnum.FreeText) && (
        <textarea
          name={`q-${question.questionId}`}
          className="w-full max-w-xl border border-zeos-grey-lt rounded-md p-2 text-zeos-black"
          rows={3}
          value={answers[question.questionId] ?? ""}
          maxLength={question.maxLength ?? undefined}
          placeholder="Optional short description"
          onChange={(event) =>
            handleFreeTextChange(question.questionId, event.target.value)
          }
        />
      )}
    </div>
  );
};
